import { Rect, Textbox, FabricImage, Line, type FabricObject } from 'fabric';
import { pageGuides, ptToMm, applyPhotoPlacement } from '@fotoboek/geometry';
import type { PageElement, PhotoFrameElement, TextElement, ShapeElement } from '@fotoboek/domain';
import type { SceneItem } from './scene';
import { PX_PER_MM } from './scene';
import type { AssetRuntime } from '../../../state/projectStore';

type DecorationElement = Extract<PageElement, { type: 'decoration' }>;

function mm(v: number): number {
  return v * PX_PER_MM;
}

export function buildPageBackground(
  item: SceneItem,
  originPx: { x: number; y: number },
): FabricObject {
  return new Rect({
    left: originPx.x,
    top: originPx.y,
    width: mm(item.sizeMm.width),
    height: mm(item.sizeMm.height),
    fill: item.backgroundColor,
    selectable: false,
    evented: false,
    hoverCursor: 'default',
  });
}

export function buildGuides(
  item: SceneItem,
  originPx: { x: number; y: number },
  flags: { bleed: boolean; trim: boolean; safe: boolean },
): FabricObject[] {
  const guides = pageGuides(item.sizeMm, item.bleedMm, item.safeMarginMm);
  const objects: FabricObject[] = [];

  const rectGuide = (
    rect: { x: number; y: number; width: number; height: number },
    color: string,
    dashed: boolean,
  ) =>
    new Rect({
      left: originPx.x + mm(rect.x),
      top: originPx.y + mm(rect.y),
      width: mm(rect.width),
      height: mm(rect.height),
      fill: 'transparent',
      stroke: color,
      strokeWidth: 1,
      strokeDashArray: dashed ? [4, 3] : undefined,
      selectable: false,
      evented: false,
      hoverCursor: 'default',
    });

  if (flags.bleed && item.bleedMm > 0) objects.push(rectGuide(guides.bleed, '#c0685f', true));
  if (flags.trim) objects.push(rectGuide(guides.trim, '#9a9284', false));
  if (flags.safe) objects.push(rectGuide(guides.safe, '#4a7f9e', true));

  return objects;
}

export function buildGutterLine(xMm: number, heightMm: number): FabricObject {
  return new Line([mm(xMm), 0, mm(xMm), mm(heightMm)], {
    stroke: '#8a8174',
    strokeWidth: 1,
    strokeDashArray: [2, 4],
    selectable: false,
    evented: false,
    hoverCursor: 'default',
  });
}

function baseElementProps(el: PageElement, originPx: { x: number; y: number }) {
  return {
    left: originPx.x + mm(el.x),
    top: originPx.y + mm(el.y),
    width: mm(el.width),
    height: mm(el.height),
    angle: el.rotation,
    opacity: el.opacity,
    selectable: !el.locked,
    lockMovementX: el.locked,
    lockMovementY: el.locked,
    lockRotation: el.locked,
    lockScalingX: el.locked,
    lockScalingY: el.locked,
    hasControls: !el.locked,
  };
}

export function buildShape(el: ShapeElement, originPx: { x: number; y: number }): FabricObject {
  if (el.shape === 'line') {
    return new Line([0, 0, mm(el.width), 0], {
      ...baseElementProps(el, originPx),
      stroke: el.stroke ?? el.fill ?? '#1a1a1a',
      strokeWidth: Math.max(1, mm(el.strokeWidthMm || 0.5)),
    });
  }
  return new Rect({
    ...baseElementProps(el, originPx),
    fill: el.fill ?? 'transparent',
    stroke: el.stroke ?? undefined,
    strokeWidth: mm(el.strokeWidthMm),
    rx: el.shape === 'ellipse' ? mm(el.width) / 2 : mm(el.cornerRadiusMm),
    ry: el.shape === 'ellipse' ? mm(el.height) / 2 : mm(el.cornerRadiusMm),
  });
}

export function buildDecoration(
  el: DecorationElement,
  originPx: { x: number; y: number },
): FabricObject {
  return new Rect({
    ...baseElementProps(el, originPx),
    fill: el.color,
    rx: 0,
    ry: 0,
  });
}

export function buildText(el: TextElement, originPx: { x: number; y: number }): FabricObject {
  return new Textbox(el.content, {
    ...baseElementProps(el, originPx),
    fontFamily: el.fontFamily,
    fontSize: ptToMm(el.fontSizePt) * PX_PER_MM,
    fontWeight: el.fontWeight,
    fontStyle: el.fontStyle,
    lineHeight: el.lineHeight,
    charSpacing: el.letterSpacingEm * 1000,
    textAlign: el.align,
    fill: el.color,
    backgroundColor: el.backgroundColor ?? undefined,
    editable: false,
    splitByGrapheme: false,
  });
}

/**
 * Non-interactive image layer showing the current cover-fit crop of a
 * photo frame. Frame geometry (position/size) lives on the *handle* object
 * (see `buildFrameHandle`) so resizing the frame never distorts the model —
 * the image simply re-covers on next render.
 */
export async function buildPhotoImageLayer(
  el: PhotoFrameElement,
  originPx: { x: number; y: number },
  asset: AssetRuntime,
  interactive: boolean,
): Promise<FabricObject> {
  const frameMm = { width: el.width, height: el.height };
  const placed = applyPhotoPlacement(
    frameMm,
    { width: asset.widthPx, height: asset.heightPx },
    el.placement,
  );
  const img = await FabricImage.fromURL(asset.objectUrl, { crossOrigin: 'anonymous' });
  const scaleX = mm(placed.width) / asset.widthPx;
  const scaleY = mm(placed.height) / asset.heightPx;
  img.set({
    left: originPx.x + mm(el.x) + mm(placed.x),
    top: originPx.y + mm(el.y) + mm(placed.y),
    scaleX,
    scaleY,
    flipX: placed.flipX,
    flipY: placed.flipY,
    angle: interactive ? 0 : el.rotation,
    opacity: el.opacity,
    selectable: interactive,
    evented: interactive,
    hasControls: false,
    hasBorders: false,
    lockRotation: true,
    lockScalingX: true,
    lockScalingY: true,
    hoverCursor: interactive ? 'move' : 'default',
    clipPath: new Rect({
      left: originPx.x + mm(el.x),
      top: originPx.y + mm(el.y),
      width: mm(el.width),
      height: mm(el.height),
      rx: mm(el.cornerRadiusMm),
      ry: mm(el.cornerRadiusMm),
      angle: interactive ? 0 : el.rotation,
      absolutePositioned: true,
    }),
  });
  return img;
}

/** Static outline shown around a frame while its photo is being panned/zoomed in crop mode. */
export function buildCropFrameOutline(
  el: PhotoFrameElement,
  originPx: { x: number; y: number },
): FabricObject {
  return new Rect({
    left: originPx.x + mm(el.x),
    top: originPx.y + mm(el.y),
    width: mm(el.width),
    height: mm(el.height),
    fill: 'transparent',
    stroke: '#2f6f6a',
    strokeWidth: 2,
    rx: mm(el.cornerRadiusMm),
    ry: mm(el.cornerRadiusMm),
    selectable: false,
    evented: false,
  });
}

/** The interactive object representing a photo frame's own bounds. */
export function buildFrameHandle(
  el: PhotoFrameElement,
  originPx: { x: number; y: number },
  hasAsset: boolean,
): FabricObject {
  return new Rect({
    ...baseElementProps(el, originPx),
    fill: hasAsset ? 'transparent' : '#e5e0d6',
    stroke: hasAsset ? 'transparent' : el.required ? '#b5473a' : '#cfc8ba',
    strokeWidth: 1,
    strokeDashArray: hasAsset ? undefined : [5, 4],
    strokeUniform: true,
    rx: mm(el.cornerRadiusMm),
    ry: mm(el.cornerRadiusMm),
  });
}
